/**
 * Handing the user a file.
 *
 * The export itself is a pure function in `@orbit-mind/export-engine`; everything here is the
 * browser's half — a blob, an anchor, and revoking the URL afterwards. That split is what lets
 * the same export run in a Cloudflare Worker later without any of this coming along.
 *
 * The scene it exports is the one on screen, complete. Not the visible portion: culling is a
 * drawing optimisation, and a file that quietly omitted whatever was scrolled out of view would
 * be the single most confusing thing this application could do.
 */
import { useCallback, useState } from 'react';

import type { MapDocument } from '@orbit-mind/domain';
import {
  exportFilename,
  MARKDOWN_MIME_TYPE,
  PDF_MIME_TYPE,
  PPTX_MIME_TYPE,
  renderMarkdown,
  renderPptx,
  sceneToSvg,
  SVG_MIME_TYPE,
} from '@orbit-mind/export-engine';
import { sceneToPng } from '@orbit-mind/export-engine/browser';
import type { Scene, TextMeasurer } from '@orbit-mind/map-engine';
import type { AppError } from '@orbit-mind/shared-types';

/**
 * Saves a blob under a name.
 *
 * An anchor with `download`, which is what every browser this ships to supports. The URL is
 * kept alive briefly rather than revoked immediately: revoking it in the same tick can beat the
 * download starting, and the failure is a silently empty file.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}

export interface ExportControls {
  readonly exportSvg: () => void;
  readonly exportPng: () => void;
  readonly exportPdf: () => void;
  /** A slide deck: the whole map on the first slide, then a slide per branch. */
  readonly exportPptx: () => void;
  /** The map as a nested list under its title. */
  readonly exportMarkdown: () => void;
  /** True while a raster is being drawn. Rasterising a large map is not instant. */
  readonly busy: boolean;
  /** The last failure, for a control that needs something to say. */
  readonly error: AppError | null;
  /**
   * Characters the PDF could not represent, replaced with `?`.
   *
   * Said out loud for the same reason as the scale: a map in a language the standard fonts do not
   * cover exports to question marks, and finding that out from the printed copy is too late.
   */
  readonly unsupportedCharacters: number;
  /**
   * Set when the image had to be made smaller than asked for.
   *
   * Told rather than hidden: someone who asked for a 3× PNG and silently got 1.6× would find out
   * when they printed it.
   */
  readonly reducedTo: number | null;
}

export function useExport(
  scene: Scene,
  document: MapDocument,
  measurer?: TextMeasurer,
): ExportControls {
  const title = document.map.title;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<AppError | null>(null);
  const [reducedTo, setReducedTo] = useState<number | null>(null);
  const [unsupportedCharacters, setUnsupported] = useState(0);

  const exportSvg = useCallback(() => {
    const svg = sceneToSvg(scene, { title });
    saveBlob(new Blob([svg], { type: SVG_MIME_TYPE }), exportFilename(title, 'svg'));
  }, [scene, title]);

  const exportPdf = useCallback(() => {
    setError(null);
    // The same measurer that sized the boxes on screen. PDF has no `text-anchor`, so centring is
    // arithmetic — and arithmetic against a width the box was never built from is text that sits
    // slightly wrong in every node.
    setBusy(true);
    void import('@orbit-mind/export-engine/pdf-unicode')
      .then(({ sceneToUnicodePdf }) =>
        sceneToUnicodePdf(scene, { title, ...(measurer === undefined ? {} : { measurer }) }),
      )
      .then((result) => {
        setUnsupported(0);
        saveBlob(
          new Blob([result.bytes as BlobPart], { type: PDF_MIME_TYPE }),
          exportFilename(title, 'pdf'),
        );
      })
      .catch((cause: unknown) => {
        setError({
          code: 'VALIDATION_FAILED',
          message: cause instanceof Error ? cause.message : 'PDF unavailable',
          retryable: false,
        });
      })
      .finally(() => {
        setBusy(false);
      });
  }, [measurer, scene, title]);

  const exportPng = useCallback(() => {
    setBusy(true);
    setError(null);
    setReducedTo(null);

    void sceneToPng(scene, { title }).then((result) => {
      setBusy(false);
      if (!result.ok) {
        setError(result.error);
        return;
      }
      if (result.value.size.clamped) setReducedTo(result.value.size.scale);
      saveBlob(result.value.blob, exportFilename(title, 'png'));
    });
  }, [scene, title]);

  const exportMarkdown = useCallback(() => {
    saveBlob(
      new Blob([renderMarkdown(document)], { type: MARKDOWN_MIME_TYPE }),
      exportFilename(title, 'md'),
    );
  }, [document, title]);

  const exportPptx = useCallback(() => {
    setBusy(true);
    setError(null);

    // The opening slide carries a picture of the whole map. A deck without it is still the deck,
    // so a drawing that fails costs the picture and nothing else.
    void sceneToPng(scene, { title })
      .then(async (png) => {
        if (!png.ok) return undefined;
        return {
          bytes: new Uint8Array(await png.value.blob.arrayBuffer()),
          width: png.value.size.width,
          height: png.value.size.height,
        };
      })
      .catch(() => undefined)
      .then((picture) => {
        setBusy(false);
        const deck = renderPptx(document, picture === undefined ? {} : { picture });
        saveBlob(
          new Blob([deck as BlobPart], { type: PPTX_MIME_TYPE }),
          exportFilename(title, 'pptx'),
        );
      });
  }, [document, scene, title]);

  return {
    exportSvg,
    exportPng,
    exportPdf,
    exportPptx,
    exportMarkdown,
    busy,
    error,
    reducedTo,
    unsupportedCharacters,
  };
}
